/**
 * A Google Fonts family fetched with `&text=` (the picker's name-only previews) arrives as one
 * `@font-face` whose `unicode-range` is just those characters. A real family has a latin face of
 * 200+ codepoints. Anything at or under this size is a preview subset, never a usable font.
 */
export const PREVIEW_SUBSET_MAX_CODEPOINTS = 64

/**
 * Number of codepoints a CSS `unicode-range` value covers, e.g. `U+4c, U+62, U+72-74` is 5 and
 * `U+4??` is 16. The CSS default, `U+0-10FFFF`, is the whole range. Unparseable tokens count as the
 * whole range, so the caller errs toward treating a face as a real font.
 */
export function unicodeRangeSize(unicodeRange?: string): number {
  if (!unicodeRange) {
    return Number.POSITIVE_INFINITY
  }
  let size = 0
  for (const rawToken of unicodeRange.split(',')) {
    const token = rawToken.trim().toLowerCase()
    if (!token.startsWith('u+')) {
      return Number.POSITIVE_INFINITY
    }
    const [first, last, ...extra] = token.slice(2).split('-')
    const isWildcard = first.includes('?')
    const low = Number.parseInt(first.replaceAll('?', '0'), 16)
    let high = low
    if (isWildcard) {
      high = Number.parseInt(first.replaceAll('?', 'f'), 16)
    } else if (last !== undefined) {
      high = Number.parseInt(last, 16)
    }
    if (extra.length > 0 || Number.isNaN(low) || Number.isNaN(high) || high < low) {
      return Number.POSITIVE_INFINITY
    }
    size += high - low + 1
  }
  return size
}

/** Whether a `FontFace` is one of the picker's name-only preview subsets. */
export function isPreviewSubsetFace(font: { unicodeRange?: string }): boolean {
  return unicodeRangeSize(font.unicodeRange) <= PREVIEW_SUBSET_MAX_CODEPOINTS
}
